import React, { useEffect, useRef, useState } from "react";
import { ImmunizationRecord } from "../models/Interfaces";
import { useTranslation } from "react-i18next";
import { DocketAPIError, getAPIClient, reportTypeFromString } from "../apiClient";
import db, { Key } from "../database/database";
import { arrayBufferToBase64 } from "../utils/reportUtils";
import { ErrorModal } from "./modals/ErrorModal";
import { Spinner } from "./spinner/Spinner";
import { refreshingAtom } from "../jotai/atoms";
import { useAtom } from "jotai";
import { fetchRecordsUntilUpdated } from "../utils/polling";

interface ReportCardProps {
  reportContents: string;
  reportTitle: string;
  onRefresh: () => void;
  onDownload: () => void;
  loading: boolean;
}

const base64ToBlob = (base64: string, mimeType = "") => {
  const byteChars = atob(base64);
  const byteNumbers = new Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

export default function ReportCard({
  reportContents,
  reportTitle,
  loading,
  onRefresh,
  onDownload,
}: ReportCardProps) {
  const { t } = useTranslation();

  const getIFrameDiv = () => {
    return (
      <div
        style={{
          height: reportContents.length > 0 ? "400px" : "",
        }}
      >
        {reportContents.length > 0 ? (
          <iframe
            key={reportContents}
            src={reportContents}
            className="aspect-video w-full h-full max-md:scale-60 max-md:origin-top-left max-md:w-[170%] max-md:h-[170%]"
          ></iframe>
        ) : (
          <iframe className="h-0"></iframe>
        )}
      </div>
    );
  };

  const sharePDF = async () => {
    const base64 = reportContents.split(",")[1]; // Removes "data:application/pdf;base64,"
    const blob = base64ToBlob(base64, "application/pdf");
    const file = new File([blob], "immunization_report.pdf", { type: blob.type });
    navigator
      .share({
        title: "Share Immunization Report",
        files: [file],
      })
      .catch((error) => {});
  };

  return (
    <>
      <div className="rounded-md bg-white p-4 flex flex-col gap-1">
        {loading ? (
          <Spinner />
        ) : (
          <>
            <div className="flex gap-1">
              <p className="title font-semibold text-black" data-testid="search-card-name">
                {reportTitle}
              </p>
              {reportContents.length > 0 && (
                <img
                  src="/assets/images/arrows_rotate_solid.svg"
                  alt="Refresh PDF"
                  className="md:hidden cursor-pointer"
                  width="20"
                  height="20"
                  onClick={onRefresh}
                />
              )}
            </div>
            {getIFrameDiv()}
            {reportContents.length > 0 ? (
              <>
                <button onClick={sharePDF} className="md:hidden btn w-fit docket-button">
                  <img src="/assets/images/share-light.svg" alt="Share PDF" className="w-4 h-4" />
                  {t("immunizations.reports_share")}
                </button>
                <button onClick={onRefresh} className="max-md:hidden btn w-fit docket-button">
                  {t("generic.refresh")}
                </button>
              </>
            ) : (
              <button onClick={onDownload} className="btn w-fit docket-button">
                {t("immunizations.reports_download")}
              </button>
            )}
          </>
        )}
      </div>
    </>
  );
}
